// src/pages/JobHistory.js
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { getAuth } from "firebase/auth";
import { collection, getDocs, updateDoc, deleteDoc, doc, addDoc, serverTimestamp, arrayUnion } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../firebaseConfig";
import "./WorkOrders.css";
import "./JobDetailsShared.css";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useNavigate, useLocation } from "react-router-dom";
import { closeOrDeleteJob, isRecurringJobRecord, getRecurringOccurrenceKey } from "../utils/recurringEditUtils";
import { addJobTotal } from "../utils/addJobTotal";
import { getOccurrencesForJob } from "../utils/getOccurrencesForJob";
import {
  isRecurring as isRecurringOccurrenceJob,
  getCompletedDateSet,
  getCanonicalKeyForScheduledKey,
  makeOccurrenceDateMatcher,
  toDateKey as toOccurrenceKey,
} from "../utils/recurringOccurrenceUtils";
import QuickJobFieldEditor from "../components/QuickJobFieldEditor";
import CollapsibleActionSection from "../components/CollapsibleActionSection";
import JobHistoryPopup from "../components/JobHistoryPopup";
import JobCompletionInvoiceModal from "../components/JobCompletionInvoiceModal";
import { refreshKeepingView } from "../utils/recurringOccurrenceUtils";
import {
  getMaterialsGroupId,
  fetchAllJobMaterials,
  loadMaterialsForJob,
  saveMaterialsForJob,
} from "../utils/materialsUtils";

// ✅ EMBEDS (adjust paths/names if needed)
import ProposalsJMC from "./ProposalsJMC";
import ProposalsPPD from "./ProposalsPPD";
import InvoicePPD from "./InvoicePPD"; // or InvoicesPPD if that’s the real component name
// ✅ Proposal status embeds
import ApprovedProposals from "./ApprovedProposals";
import CompletedProposals from "./CompletedProposals";
import SentProposals from "./SentProposals";

import PPDApprovedProposals from "./PPDApprovedProposals";
import PPDCompletedProposals from "./PPDCompletedProposals";
import PPDSentProposals from "./PPDSentProposals";

import PPDApprovedInvoices from "./PPDApprovedInvoices";
import PPDCompletedInvoices from "./PPDCompletedInvoices";
import PPDSentInvoices from "./PPDSentInvoices";

import Notebook from "./Notebook";
import Scheduling from "./Scheduling"; 

// ✅ MORE MENU EMBEDS (create/import these pages if not built yet)
import Vendors from "./Vendors";
import Contractors from "./Contractors";
import Vehicles from "./Vehicles";
import Supplies from "./Supplies";
import Reports from "./Reports";



const LoadingSpinner = ({ label = "Saving" }) => (
  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
    <style>{`@keyframes ppd-note-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        border: "2px solid rgba(255,255,255,0.65)",
        borderTopColor: "#111827",
        borderRadius: "50%",
        display: "inline-block",
        animation: "ppd-note-spin 0.8s linear infinite",
      }}
    />
    {label}
  </span>
);


const parseProposalDescriptionBlocks = (description = "") => {
  const source = String(description || "")
    .replace(/\r\n?/g, "\n")
    .replace(/\(\(\s*B\s*\)\)/g, "((b))")
    .replace(/\(\(\s*I\s*\)\)/g, "((i))")
    .replace(/\(\(\s*R\s*\)\)/g, "((r))");

  if (!source.trim()) return [];

  const markerRegex = /\(\((b|i|r|-)\)\)/gi;
  const matches = [...source.matchAll(markerRegex)];
  const blocks = [];

  if (!matches.length) {
    return source
      .split(/\n{2,}|\n/)
      .map((text) => ({ type: "r", text: text.trim() }))
      .filter((block) => block.text);
  }

  const leading = source.slice(0, matches[0].index).trim();
  if (leading) blocks.push({ type: "r", text: leading });

  matches.forEach((match, index) => {
    const start = match.index + match[0].length;
    const end = index + 1 < matches.length ? matches[index + 1].index : source.length;
    const text = source.slice(start, end).trim();
    if (text) blocks.push({ type: match[1].toLowerCase(), text });
  });

  return blocks;
};

const isDescriptionHeading = (text = "") => {
  const clean = String(text).replace(/\s+/g, " ").trim();
  if (!clean) return false;
  if (clean.length <= 85 && !/[.!?]$/.test(clean)) return true;

  return /^(services?|introduction|scope|scope of service|treatment|treatment method|treatment areas?|high[- ]risk|inspection|service frequency|property management recommendations?|recommendations?|work to be performed|service steps?|application|areas? (included|treated))/i.test(clean);
};

const renderProposalJobDescription = (description, { compact = false } = {}) => {
  const blocks = parseProposalDescriptionBlocks(description);

  if (!blocks.length) {
    return <div style={{ color: "#6b7280" }}>No job description provided.</div>;
  }

  return blocks.map((block, index) => {
    const text = block.text.replace(/\s*\n\s*/g, " ").replace(/\s{2,}/g, " ").trim();
    if (!text) return null;

    if (block.type === "-") {
      return (
        <div key={index} style={{ display: "flex", gap: 8, margin: "5px 0 5px 14px", lineHeight: 1.5 }}>
          <span aria-hidden="true">•</span>
          <span>{text}</span>
        </div>
      );
    }

    if (block.type === "b" && isDescriptionHeading(text)) {
      return (
        <div
          key={index}
          style={{
            fontWeight: 800,
            fontSize: compact ? 16 : 17,
            marginTop: index === 0 ? 0 : 14,
            marginBottom: 6,
            lineHeight: 1.35,
          }}
        >
          {text}
        </div>
      );
    }

    if (block.type === "b") {
      return <div key={index} style={{ fontWeight: 700, marginBottom: 8, lineHeight: 1.55 }}>{text}</div>;
    }

    if (block.type === "i") {
      return <div key={index} style={{ fontStyle: "italic", marginBottom: 8, lineHeight: 1.55 }}>{text}</div>;
    }

    return <div key={index} style={{ marginBottom: 8, lineHeight: 1.55 }}>{text}</div>;
  });
};

const getTechDirectionBlocks = (description = "") => {
  const blocks = parseProposalDescriptionBlocks(description);
  if (!blocks.length) return [];

  const includeHeading = /(scope|treatment|high[- ]risk|inspection|application|service steps?|work to be performed|areas? (included|treated)|installation|repair|removal|exclusion|monitoring)/i;
  const excludeHeading = /(introduction|about us|property management recommendations?|recommendations?|service frequency|terms|warranty|pricing|investment|acceptance)/i;

  const selected = [];
  let includeCurrentSection = false;

  blocks.forEach((block) => {
    const clean = block.text.replace(/\s+/g, " ").trim();
    const heading = block.type === "b" && isDescriptionHeading(clean);

    if (heading) {
      if (excludeHeading.test(clean)) {
        includeCurrentSection = false;
        return;
      }

      includeCurrentSection = includeHeading.test(clean);
      if (includeCurrentSection) selected.push(block);
      return;
    }

    if (includeCurrentSection) selected.push(block);
  });

  if (selected.length) return selected;

  return blocks.filter((block) => {
    const clean = block.text.replace(/\s+/g, " ").trim();
    return (
      block.type === "-" ||
      /\b(inspect|inspection|treat|treatment|apply|application|install|repair|remove|service|monitor|bait|spray|larvicide|mist|equipment|areas? may include)\b/i.test(clean)
    );
  }).slice(0, 12);
};

const renderTechDirections = (description = "") => {
  const blocks = getTechDirectionBlocks(description);
  if (!blocks.length) return renderProposalJobDescription(description, { compact: true });

  return blocks.map((block, index) => {
    const text = block.text.replace(/\s*\n\s*/g, " ").replace(/\s{2,}/g, " ").trim();
    if (!text) return null;

    if (block.type === "-") {
      return (
        <div key={index} style={{ display: "flex", gap: 8, margin: "5px 0 5px 14px", lineHeight: 1.5 }}>
          <span aria-hidden="true">•</span>
          <span>{text}</span>
        </div>
      );
    }

    if (block.type === "b" && isDescriptionHeading(text)) {
      return (
        <div key={index} style={{ fontWeight: 800, fontSize: 16, marginTop: index ? 12 : 0, marginBottom: 5 }}>
          {text}
        </div>
      );
    }

    return <div key={index} style={{ marginBottom: 8, lineHeight: 1.5 }}>{text}</div>;
  });
};


const JobHistory = () => {
  const navigate = useNavigate();

  const location = useLocation();

  const topNavBtnStyle = (path) => {
    const isActive = location.pathname === path;
  
    return {
      padding: "10px 14px",
      borderRadius: 10,
      border: "1px solid #1d4ed8",
      background: isActive ? "#1d4ed8" : "#e0e7ff",
      color: isActive ? "#fff" : "#0f172a",
      fontWeight: 800,
      cursor: "pointer",
    };
  };
   // ===================== HUB UI STATE =====================
  // The open tab is remembered in sessionStorage (same pattern as Home.js)
  // so a browser refresh lands back on the same embedded tab instead of
  // resetting to Job History. Unknown/stale values fall back to job-history.
  const JOB_HISTORY_ACTIVE_TAB_STORAGE_KEY = "priority_jobhistory_active_tab";
  const VALID_JOB_HISTORY_TABS = [
    "job-history",
    "scheduling",
    "proposals",
    "notebook",
    "reports",
    "vendors",
    "contractors",
    "vehicles",
    "supplies",
  ];

  const [activeTab, setActiveTab] = useState(() => {
    try {
      const stored = sessionStorage.getItem(JOB_HISTORY_ACTIVE_TAB_STORAGE_KEY);
      return VALID_JOB_HISTORY_TABS.includes(stored) ? stored : "job-history";
    } catch (e) {
      return "job-history";
    }
  });
// job-history | scheduling | proposals | notebook | reports | vendors | contractors | vehicles | supplies

  useEffect(() => {
    try {
      sessionStorage.setItem(JOB_HISTORY_ACTIVE_TAB_STORAGE_KEY, activeTab);
    } catch (e) {
      // sessionStorage unavailable (e.g. private browsing) — safe to ignore
    }
  }, [activeTab]);

  
  // ✅ TOP proposal tab (the 2 warm buttons)
  const [activeProposalTab, setActiveProposalTab] = useState("ppd");
  // jmc | dcs | invoiceppd
  
  // ✅ which status page is selected inside JMC/DCS
  const [activeProposalStatus, setActiveProposalStatus] = useState("approved");
  // approved | completed | sent
  
// ✅ which view is showing inside JMC/DCS (main page vs status pages)
const [proposalView, setProposalView] = useState("main");
// main | approved | completed | sent

const [invoiceView, setInvoiceView] = useState("main");
// main | approved | completed | sent

const [activeInvoiceStatus, setActiveInvoiceStatus] = useState("approved");
// approved | completed | sent


  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const navTabStyle = (isActive) => ({
    padding: "10px 14px",
    borderRadius: "10px 10px 0 0",
    border: "1px solid #1d4ed8",
    borderBottom: isActive ? "1px solid white" : "1px solid #1d4ed8",
    background: isActive ? "#1d4ed8" : "#93c5fd",
    color: isActive ? "white" : "#0f172a",
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  });

  const chipBtn = (isActive) => ({
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #1d4ed8",
    background: isActive ? "#1d4ed8" : "#93c5fd",
    color: isActive ? "#fff" : "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
  });

  const sideMenuBtn = (isActive) => ({
    width: "100%",
    padding: "12px 12px",
    borderRadius: 10,
    border: "1px solid #1d4ed8",
    background: isActive ? "#1d4ed8" : "#e0e7ff",
    color: isActive ? "#fff" : "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
    textAlign: "left",
  });
  
  // ✅ Warm colored proposal chips (centered row uses this)
  const proposalChipBtn = (type, isActive) => {
    const palette = {

      ppd: { bg: "#f87171", border: "#991b1b", text: "#0f172a" },       // warm red
      invoiceppd: { bg: "#facc15", border: "#a16207", text: "#0f172a" } // amber/yellow
    };

    const p = palette[type] || { bg: "#93c5fd", border: "#1d4ed8", text: "#0f172a" };

    return {
      padding: "12px 16px",
      borderRadius: 12,
      border: `2px solid ${isActive ? p.border : "transparent"}`,
      background: p.bg,
      color: p.text,
      fontWeight: 900,
      cursor: "pointer",
      boxShadow: isActive ? "0 0 0 3px rgba(29,78,216,0.15)" : "none",
      transform: isActive ? "translateY(-1px)" : "none",
    };
  };

  const embedWrap = {
    border: "1px solid #1d4ed8",
    borderRadius: 12,
    padding: 12,
    background: "#fff",
    minHeight: 500,
  
    // key for smaller screens
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    overflowY: "auto",
  
    // helps flex/grid children shrink correctly
    minWidth: 0,
  
    // optional if you want a viewport-based height inside proposals
    maxHeight: "calc(100vh - 320px)",
  };

  // ===================== YOUR EXISTING JOB HISTORY STATE =====================
  const [jobs, setJobs] = useState([]);

  // left-side site list controls
  const [siteSearch, setSiteSearch] = useState("");
  const [siteSort, setSiteSort] = useState("siteNameAsc"); // siteNameAsc | siteNameDesc | countDesc | countAsc

  // selected site -> show its work history
  const [selectedSite, setSelectedSite] = useState(null);
  const [showAllJobs, setShowAllJobs] = useState(false);

  // right-side job list controls
  const [jobSearch, setJobSearch] = useState("");
  const [jobSort, setJobSort] = useState("startDateDesc"); // startDateDesc | startDateAsc | woAsc | woDesc | statusAsc

  // modal
    // modal
    const [selectedJob, setSelectedJob] = useState(null);

    // Lock background scrolling while the Job Details popup is open so the
    // popup can't be scrolled past — it has to be closed to interact with
    // the page behind it.
    useEffect(() => {
      if (!selectedJob) return;
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }, [selectedJob]);
    const [isEditingJob, setIsEditingJob] = useState(false);
    const [showHistoryPopup, setShowHistoryPopup] = useState(false);
    const [editedJob, setEditedJob] = useState(null);
    const [isSavingJob, setIsSavingJob] = useState(false);

  const [jobStartDate, setJobStartDate] = useState("");
  const [jobEndDate, setJobEndDate] = useState("");
  const [jobTotals, setJobTotals] = useState({});
  const [language, setLanguage] = useState("en");

  const [showAddNote, setShowAddNote] = useState(false);
const [newNoteText, setNewNoteText] = useState("");
const [newNoteFiles, setNewNoteFiles] = useState([]);
const [isSavingNote, setIsSavingNote] = useState(false);

const [previewImage, setPreviewImage] = useState(null);

  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [showTotalPopup, setShowTotalPopup] = useState(false);
  const [totalInput, setTotalInput] = useState("");
  const [showReceiptPopup, setShowReceiptPopup] = useState(false);
  const [receiptAmount, setReceiptAmount] = useState("");
  const [receiptNote, setReceiptNote] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [savingReceipt, setSavingReceipt] = useState(false);
  const [showMaterialsPopup, setShowMaterialsPopup] = useState(false);
  const [materialsDraft, setMaterialsDraft] = useState([]);
  const [jobMaterials, setJobMaterials] = useState({});
  const getMaterialsForJob = (job) =>
    jobMaterials[getMaterialsGroupId(job)] || [];

  // ✅ user info for "Welcome to the Portal" + Role:
  const [userRole, setUserRole] = useState("");
  const [userFullName, setUserFullName] = useState("");
  const [usersList, setUsersList] = useState([]);
  const [propertiesList, setPropertiesList] = useState([]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) return;
  
        const usersRef = collection(db, "users");
        const snap = await getDocs(usersRef);
  
        const users = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const me = users.find((u) => u.id === currentUser.uid);
  
        if (me) {
          setUserRole(me.role || "");
          setUserFullName(`${me.firstName || ""} ${me.lastName || ""}`.trim());
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };
  
    fetchUserData();
  }, []);
  
  useEffect(() => {
    const fetchSupportData = async () => {
      try {
        const [usersSnapshot, propertiesSnapshot] = await Promise.all([
          getDocs(collection(db, "users")),
          getDocs(collection(db, "properties")),
        ]);

        setUsersList(usersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        setPropertiesList(
          propertiesSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }))
        );
      } catch (err) {
        console.error("Error fetching edit popup support data:", err);
      }
    };

    fetchSupportData();
  }, []);
  
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const jobsRef = collection(db, "jobs");
        const addItemRef = collection(db, "addItem");
        const jobTotalsRef = collection(db, "job_totals");

        const [jobsSnapshot, addItemSnapshot, totalsSnapshot] = await Promise.all([
          getDocs(jobsRef),
          getDocs(addItemRef),
          getDocs(jobTotalsRef),
        ]);

        const jobsData = jobsSnapshot.docs.map((d) => ({
          id: d.id,
          __col: "jobs",
          ...d.data(),
        }));
        
        const addItemData = addItemSnapshot.docs.map((d) => ({
          id: d.id,
          __col: "addItem",
          ...d.data(),
        }));

        const totalsMap = {};
        totalsSnapshot.docs.forEach((d) => {
          const data = d.data();
          if (data.jobId && data.total !== undefined) {
            totalsMap[data.jobId] = parseFloat(data.total) || 0;
          }
        });

        setJobs([...jobsData, ...addItemData]);
        setJobTotals(totalsMap);
      } catch (error) {
        console.error("Error fetching jobs:", error);
      }
    };

    fetchJobs();
  }, []);

  const handleAdminPortalClick = () => {
    if (!canAccessAdminPortal) {
      window.alert("🚫 Access denied: You don’t have permission to view the Admin Portal.");
      navigate("/"); // back to Home page
      return;
    }
    navigate("/SupervisorDashboard");
  };
  
  const normalize = (v) => (v || "").toString().toLowerCase();
  const getJobTotal = (job) => {
    const wo = job?.wo !== undefined && job?.wo !== null ? String(job.wo) : "";
    return jobTotals[wo] || 0;
  };

  const isSameMonth = (date, ref) => moment(date).isSame(ref, "month");
  const isSameYear = (date, ref) => moment(date).isSame(ref, "year");

  const sites = useMemo(() => {
    const map = new Map();

    jobs.forEach((j) => {
      const siteName = (j.siteName || "").trim();
      if (!siteName) return;

      const existing = map.get(siteName) || {
        siteName,
        count: 0,
        lastActivityDate: null,
      };

      existing.count += 1;

      const candidate = j.endDate || j.startDate || j.createdAt?.toDate?.() || j.timestamp || null;

      let candidateMoment = null;
      if (candidate) {
        candidateMoment = moment(candidate);
        if (!candidateMoment.isValid()) candidateMoment = null;
      }

      if (candidateMoment) {
        if (!existing.lastActivityDate || candidateMoment.isAfter(existing.lastActivityDate)) {
          existing.lastActivityDate = candidateMoment;
        }
      }

      map.set(siteName, existing);
    });

    let list = Array.from(map.values());

    const term = normalize(siteSearch);
    if (term) list = list.filter((s) => normalize(s.siteName).includes(term));

    list.sort((a, b) => {
      if (siteSort === "siteNameAsc") return a.siteName.localeCompare(b.siteName);
      if (siteSort === "siteNameDesc") return b.siteName.localeCompare(a.siteName);
      if (siteSort === "countDesc") return (b.count || 0) - (a.count || 0);
      if (siteSort === "countAsc") return (a.count || 0) - (b.count || 0);
      return 0;
    });

    return list;
  }, [jobs, siteSearch, siteSort]);

  // ===================== RECURRING → ONE ROW PER DATE =====================
  // Job History is the HISTORICAL view, so a recurring job should list every
  // date it actually ran, newest first — not a single row for the whole
  // series (which is why only two or three ever showed up here).
  //
  // The NEWEST occurrence is flagged __isSeriesTopRow. That row is the
  // "series row": its Details popup shows the notes for the whole series.
  // Every older row shows only the notes recorded on its own date.
  const HISTORY_LOOKBACK_MONTHS = 24;

  const expandJobIntoOccurrences = (job) => {
    if (!isRecurringOccurrenceJob(job)) return [job];

    const startKey = toOccurrenceKey(job.startDate);
    if (!startKey) return [job];

    // Look back far enough to cover real history, forward only to today —
    // history shouldn't invent occurrences that haven't happened yet.
    const rangeStart = moment.max(
      moment(startKey, "YYYY-MM-DD"),
      moment().subtract(HISTORY_LOOKBACK_MONTHS, "months")
    );
    const rangeEnd = moment();

    if (rangeEnd.isBefore(rangeStart, "day")) return [job];

    const isExcluded = makeOccurrenceDateMatcher(job, job.excludedDates);
    const isCompleted = makeOccurrenceDateMatcher(job, job.completedDates);
    const isInvoiced = makeOccurrenceDateMatcher(job, job.invoicedDates);

    // Fold completions recorded under a rescheduled day back to the canonical
    // date, so a moved occurrence contributes ONE history row rather than
    // showing up a second time under the day it moved to.
    const completedCanonical = Array.from(getCompletedDateSet(job)).map(
      (dateKey) => getCanonicalKeyForScheduledKey(job, dateKey) || dateKey
    );

    const dates = (getOccurrencesForJob(job, rangeStart, rangeEnd) || [])
      .map(toOccurrenceKey)
      .filter(Boolean);

    // A date can be completed but fall outside the generated range (e.g. the
    // recurrence rule was edited later). Never lose real history.
    const allDates = Array.from(new Set([...dates, ...completedCanonical]))
      .filter((dateKey) => !isExcluded(dateKey))
      .sort()
      .reverse(); // newest first — index 0 is the top row

    if (!allDates.length) return [job];

    return allDates.map((dateKey, index) => ({
      ...job,
      startDate: dateKey,
      endDate: dateKey, // single-day occurrence, never a span
      occurrenceDate: dateKey,
      originalOccurrenceDate: dateKey,
      status: isInvoiced(dateKey)
        ? "Invoice Sent"
        : isCompleted(dateKey)
        ? "Completed"
        : job.status,
      __rowKey: `${job.id}__${dateKey}`,
      __occurrenceRow: true,
      __isSeriesTopRow: index === 0,
    }));
  };

  const expandedHistoryJobs = useMemo(
    () => jobs.flatMap(expandJobIntoOccurrences),
    [jobs]
  );

  const selectedSiteJobs = useMemo(() => {
    let data = [];

    if (showAllJobs) {
      data = [...expandedHistoryJobs];
    } else {
      if (!selectedSite) return [];
      data = expandedHistoryJobs.filter(
        (j) => (j.siteName || "").trim() === selectedSite
      );
    }

    const term = normalize(jobSearch);
    if (term) {
      data = data.filter((job) => {
        return (
          normalize(job.siteName).includes(term) ||
          normalize(job.management).includes(term) ||
          normalize(job.scope).includes(term) ||
          normalize(job.jobDescription).includes(term) ||
          normalize(job.city).includes(term) ||
          normalize(job.address).includes(term) ||
          normalize(job.wo).includes(term) ||
          normalize(job.techAssign).includes(term) ||
          normalize(job.serviceType).includes(term) ||
          normalize(job.status).includes(term)
        );
      });
    }

    if (jobStartDate || jobEndDate) {
      data = data.filter((job) => {
        if (!job.startDate) return false;
        const d = moment(job.startDate);
        if (jobStartDate && d.isBefore(moment(jobStartDate))) return false;
        if (jobEndDate && d.isAfter(moment(jobEndDate))) return false;
        return true;
      });
    }

    data.sort((a, b) => {
      if (jobSort === "startDateDesc") return new Date(b.startDate || 0) - new Date(a.startDate || 0);
      if (jobSort === "startDateAsc") return new Date(a.startDate || 0) - new Date(b.startDate || 0);
      if (jobSort === "woAsc") return (a.wo || "").toString().localeCompare((b.wo || "").toString());
      if (jobSort === "woDesc") return (b.wo || "").toString().localeCompare((a.wo || "").toString());
      if (jobSort === "statusAsc") return (a.status || "").toString().localeCompare((b.status || "").toString());
      return 0;
    });

    return data;
  }, [expandedHistoryJobs, selectedSite, showAllJobs, jobSearch, jobSort, jobStartDate, jobEndDate]);

  // Recurring jobs now contribute one ROW PER DATE, but the job total is
  // stored once on the shared doc. Summing every row would multiply the
  // money by the number of occurrences, so financial rollups count each
  // series exactly once (its top row) while the list still shows all dates.
  const billableRows = useMemo(
    () => selectedSiteJobs.filter((j) => !j.__occurrenceRow || j.__isSeriesTopRow),
    [selectedSiteJobs]
  );

  const siteViewTotal = billableRows.reduce((sum, j) => sum + getJobTotal(j), 0);
  const siteViewNetTotal = siteViewTotal;

  const { siteMonthlyTotal, siteYTDTotal } = useMemo(() => {
    const now = moment();

    const monthly = billableRows.reduce((sum, j) => {
      if (!j.startDate) return sum;
      if (!isSameMonth(j.startDate, now)) return sum;
      return sum + getJobTotal(j);
    }, 0);

    const ytd = billableRows.reduce((sum, j) => {
      if (!j.startDate) return sum;
      if (!isSameYear(j.startDate, now)) return sum;
      return sum + getJobTotal(j);
    }, 0);

    return { siteMonthlyTotal: monthly, siteYTDTotal: ytd };
  }, [billableRows, jobTotals]);

  const getBackgroundColor = (job) => {
    if (!job.startDate) return "white";

    const startDate = moment(job.startDate);
    const endDate = job.endDate ? moment(job.endDate) : null;
    const now = moment();
    const diffDaysStart = now.diff(startDate, "days");
    const diffDaysEnd = endDate ? now.diff(endDate, "days") : null;

    if ((diffDaysEnd && diffDaysEnd > 1) || (!endDate && diffDaysStart > 1)) return "red";

    switch (job.serviceType) {
      case "Proposal":
        return "#FFC0CB";
      case "Meeting":
        return "#98FB98";
      case "Action Items":
        return "#ADD8E6";
      case "Inspection":
        return "#FFD700";
      default:
        return "white";
    }
  };
  const translations = {
    en: {
      welcome: "Welcome to the Portal",
      role: "Role",
    },
    es: {
      welcome: "Bienvenido al Portal",
      role: "Rol",
    },
  };
  
  const t = (key) => translations[language]?.[key] || key;
    // ✅ Admin check (adjust roles if yours are named differently)
    const isAdmin = ["admin", "superadmin", "owner"].includes((userRole || "").toLowerCase());

    // ✅ Optional: for money formatting (prevents crashing if undefined)
    const money = (n) => `$${(Number(n) || 0).toLocaleString()}`;
  
    const canAccessAdminPortal = ["Admin", "Manager"].includes(userRole);

    const canSeeSupervisorNotes = ["Admin", "Manager", "Assistant"].includes(userRole);

    const serviceTypes = [
      "Action Items",
      "Inspection",
      "Meeting",
      "Proposal",
      "One-Time Service",
      "BED - Bed Bug Treatment",
      "HTT - Home Termite Treatment",
      "MPC - Monthly Pest Control",
      "QPC - Quarterly Pest Control",
      "RBS - Rodent Bait Station Service",
      "SEN - Termite Monitoring Station Service",
      "STT - Settlement Termite Treatment",
      "WDI - Wood Destroying Insect Inspection",
      "WLW - Wildlife Removal/Exclusion",
      "WPC - Warranty Pest Control",
      "WYG - Warranty Yard Guard",
      "YG - Yard Guard",
    ];

    const statusOptions = [
      "Active",
      "On Hold",
      "Completed",
      "Invoice Sent",
      "Proposal Sent",
      "Monthly",
    ];

    const timeWindowOptions = [
      "ANY",
      "8AM - 10AM",
      "9AM - 11AM",
      "10AM - 12PM",
      "11AM - 1PM",
      "12PM - 2PM",
      "1PM - 3PM",
      "2PM - 4PM",
      "3PM - 5PM",
      "4PM - 6PM",
      "Custom Time",
    ];

    const normalizeTechAssign = (value) => {
      if (Array.isArray(value)) return value.length ? value : [""];
      if (typeof value === "string" && value.trim()) {
        return value
          .split(",")
          .map((name) => name.trim())
          .filter(Boolean);
      }
      return [""];
    };

    const cleanTechAssign = (value) =>
      normalizeTechAssign(value).map((name) => name.trim()).filter(Boolean);

    const normalizeEditableJob = (job) => ({
      ...job,
      techAssign: normalizeTechAssign(job?.techAssign),
      startTimeMode:
        job?.startTime && /^\d{2}:\d{2}$/.test(job.startTime)
          ? "custom"
          : "preset",
    });

    const handleEditSiteChange = (siteName) => {
      const property = propertiesList.find(
        (item) =>
          item.propertyName === siteName ||
          item.siteName === siteName
      );

      setEditedJob((prev) => ({
        ...(prev || {}),
        siteName,
        address: property?.address || prev?.address || "",
        city: property?.city || prev?.city || "",
        state: property?.state || prev?.state || "",
        zip: property?.zip || prev?.zip || "",
      }));
    };

    const addEditedTechnicianField = () => {
      setEditedJob((prev) => ({
        ...(prev || {}),
        techAssign: [...normalizeTechAssign(prev?.techAssign), ""],
      }));
    };

    const removeEditedTechnicianField = (indexToRemove) => {
      setEditedJob((prev) => {
        const nextTechs = normalizeTechAssign(prev?.techAssign).filter(
          (_, index) => index !== indexToRemove
        );

        return {
          ...(prev || {}),
          techAssign: nextTechs.length ? nextTechs : [""],
        };
      });
    };

    const updateEditedTechnicianField = (indexToUpdate, value) => {
      setEditedJob((prev) => {
        const nextTechs = normalizeTechAssign(prev?.techAssign);
        nextTechs[indexToUpdate] = value;

        return {
          ...(prev || {}),
          techAssign: nextTechs,
        };
      });
    };

    const addSupervisorNote = async () => {
      if (!selectedJob?.id) return;
    
      if (!canSeeSupervisorNotes) {
        alert("You do not have permission to add supervisor notes.");
        return;
      }
    
      const text = window.prompt("Add supervisor note:");
      if (!text || !text.trim()) return;
    
      const colName = selectedJob.__col || "jobs";
    
      const newSupervisorNote = {
        id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
        text: text.trim(),
        author: userFullName || "User",
        authorRole: userRole || "",
        createdAt: new Date().toISOString(),
        checked: false,
        checkedBy: "",
        checkedAt: "",
      };
    
      const updatedSupervisorNotes = Array.isArray(selectedJob.supervisorNotes)
        ? [...selectedJob.supervisorNotes, newSupervisorNote]
        : [newSupervisorNote];
    
      await updateDoc(doc(db, colName, selectedJob.id), {
        supervisorNotes: updatedSupervisorNotes,
      });
    
      setJobs((prev) =>
        prev.map((job) =>
          job.id === selectedJob.id && (job.__col || "jobs") === colName
            ? { ...job, supervisorNotes: updatedSupervisorNotes }
            : job
        )
      );
    
      setSelectedJob((prev) => ({
        ...prev,
        supervisorNotes: updatedSupervisorNotes,
      }));
    };

    const closeModal = () => {
      setSelectedJob(null);
      setEditedJob(null);
      setIsEditingJob(false);
      setIsSavingJob(false);
    
      setShowAddNote(false);
      setNewNoteText("");
      setNewNoteFiles([]);
      setIsSavingNote(false);
      setPreviewImage(null);
    };

  const exportSiteToExcel = () => {
    if (!selectedSite && !showAllJobs) return;

    const rows = selectedSiteJobs.map((job) => ({
      Site: job.siteName,
      WO: job.wo,
      Status: job.status,
      ServiceType: job.serviceType,
      StartDate: job.startDate,
      EndDate: job.endDate,
      Revenue: getJobTotal(job),
      Tech: Array.isArray(job.techAssign) ? job.techAssign.join(", ") : job.techAssign,
      Scope: job.scope,
      Description: job.jobDescription || job.description,
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Job History");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileName = showAllJobs ? "All_Jobs_History.xlsx" : `${selectedSite}_Job_History.xlsx`;
    saveAs(blob, fileName);
  };

  const fmtDate = (d) =>
    d ? moment(d).format("MM-DD-YYYY") : "N/A";
  
  const fmtTime = (t) => (t ? t : "N/A");
  
  const renderRecurrence = (job) => {
    const r = job?.recurrence;
    if (!r || r.type === "none") return "One-Time";
  
    if (r.type === "customDates") {
      return `Custom Dates (${(r.customDates || []).length})`;
    }
  
    return r.type.charAt(0).toUpperCase() + r.type.slice(1);
  };
  
  const onEditJobChange = (name, value) => {
    setEditedJob((prev) => ({ ...(prev || {}), [name]: value }));
  };
  
  const handleDeleteJob = async () => {
    if (!selectedJob?.id) return;

    const colName = selectedJob.__col || "jobs";

    try {
      const result = await closeOrDeleteJob({
        job: selectedJob,
        collectionName: colName,
      });

      if (result.mode === "cancel") return;

      if (result.mode === "deleted") {
        setJobs((prev) =>
          prev.filter((j) => !(j.id === selectedJob.id && (j.__col || "jobs") === colName))
        );

        if (selectedSite) {
          const siteStillExists = jobs.some(
            (j) =>
              j.id !== selectedJob.id &&
              (j.siteName || "").trim() === selectedSite
          );

          if (!siteStillExists) {
            setSelectedSite(null);
          }
        }

        closeModal();
        alert("Job deleted successfully.");
        return;
      }

      // "occurrence" or "future" - the record still exists, just updated.
      setJobs((prev) =>
        prev.map((j) =>
          j.id === selectedJob.id && (j.__col || "jobs") === colName
            ? { ...j, ...result.updatedJob }
            : j
        )
      );
      closeModal();
      alert(
        result.mode === "future"
          ? "This job's future occurrences have been closed. Its history is unchanged."
          : "This occurrence has been closed."
      );
    } catch (error) {
      console.error("Error closing job:", error);
      alert("Failed to close this job.");
    }
  };

  const closeVerificationPopup = () => setShowVerificationPopup(false);
  // Complete Job & Create Invoice form.
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [completionSubmitted, setCompletionSubmitted] = useState(false);

  const markJobCompleted = async () => {
    if (!selectedJob?.id) return;

    const colName = selectedJob.__col || "jobs";
    const jobRef = doc(db, colName, selectedJob.id);
    const occurrenceKey = getRecurringOccurrenceKey(selectedJob);
    const recurring = isRecurringJobRecord(selectedJob);

    try {
      if (recurring) {
        await updateDoc(jobRef, {
          completedDates: arrayUnion(occurrenceKey),
          [`completedAtByDate.${occurrenceKey}`]: new Date().toISOString(),
        });
      } else {
        await updateDoc(jobRef, {
          status: "Completed",
          completedDates: arrayUnion(occurrenceKey),
          completedAt: serverTimestamp(),
        });
      }

      setJobs((prev) =>
        prev.map((j) =>
          j.id === selectedJob.id && (j.__col || "jobs") === colName
            ? {
                ...j,
                status: recurring ? j.status : "Completed",
                completedDates: [...(j.completedDates || []), occurrenceKey],
                completedAt: recurring ? j.completedAt : new Date().toISOString(),
              }
            : j
        )
      );

      closeModal();
      closeVerificationPopup();
    } catch (err) {
      console.error("Error completing job:", err);
      alert("Could not mark job completed.");
    }
  };

  const continueJob = async () => {
    if (!selectedJob?.id) return;

    const nextDate = window.prompt(
      "Enter the new service date (YYYY-MM-DD):",
      moment(selectedJob.startDate).add(1, "day").format("YYYY-MM-DD")
    );

    if (!nextDate) return;

    const copiedJob = {
      ...selectedJob,
      startDate: nextDate,
      endDate: nextDate,
      recurrence: { type: "none" },
      status: "Active",
      completedDates: [],
      invoicedDates: [],
      occurrenceNotes: {},
      previousJobId: selectedJob.id,
      materialsGroupId: getMaterialsGroupId(selectedJob),
      createdAt: new Date().toISOString(),
    };

    if (copiedJob.jobPlan) {
      copiedJob.jobPlan = { ...copiedJob.jobPlan };
      delete copiedJob.jobPlan.materials;
    }

    delete copiedJob.id;
    delete copiedJob.__col;
    delete copiedJob.__renderDate;
    delete copiedJob.occurrenceDate;
    delete copiedJob.originalOccurrenceDate;

    const newRef = await addDoc(collection(db, "jobs"), copiedJob);
    const newJob = { id: newRef.id, __col: "jobs", ...copiedJob };

    setJobs((prev) => [...prev, newJob]);
    setSelectedJob(newJob);
  };

  const openTotalPopup = () => {
    if (!selectedJob) return;
    setTotalInput(getJobTotal(selectedJob) || "");
    setShowTotalPopup(true);
  };

  const saveTotal = async () => {
    if (!selectedJob || totalInput === "") return;

    const colName = selectedJob.__col || "jobs";
    const jobKey = String(selectedJob.wo ?? selectedJob.id);

    await addJobTotal({
      jobId: jobKey,
      wo: selectedJob.wo ? String(selectedJob.wo) : null,
      total: Number(totalInput),
      source: "job",
    });

    // Keep the legacy job.estimatedTotal field in sync too, since some
    // other parts of the system (e.g. the invoice-send cloud function)
    // still read/write that field directly.
    await updateDoc(doc(db, colName, selectedJob.id), {
      estimatedTotal: Number(totalInput),
    });

    setJobTotals((prev) => ({
      ...prev,
      [jobKey]: Number(totalInput),
    }));

    setJobs((prev) =>
      prev.map((job) =>
        job.id === selectedJob.id && (job.__col || "jobs") === colName
          ? { ...job, estimatedTotal: Number(totalInput) }
          : job
      )
    );

    setSelectedJob((prev) => ({
      ...prev,
      estimatedTotal: Number(totalInput),
    }));

    setShowTotalPopup(false);
  };

  const openReceiptPopup = () => {
    setReceiptAmount("");
    setReceiptNote("");
    setReceiptFile(null);
    setShowReceiptPopup(true);
  };

  const saveReceipt = async () => {
    if (!selectedJob?.id || !receiptAmount) return;

    const colName = selectedJob.__col || "jobs";
    setSavingReceipt(true);

    try {
      let fileURL = "";
      let fileName = "";

      if (receiptFile) {
        const receiptRef = ref(
          getStorage(),
          `jobReceipts/${selectedJob.id}/${Date.now()}_${receiptFile.name}`
        );
        await uploadBytes(receiptRef, receiptFile);
        fileURL = await getDownloadURL(receiptRef);
        fileName = receiptFile.name;
      }

      const receipt = {
        amount: Number(receiptAmount),
        note: receiptNote.trim(),
        fileURL,
        fileName,
        createdAt: new Date().toISOString(),
        addedBy: userFullName || "User",
      };

      const receipts = [
        ...(Array.isArray(selectedJob.receipts) ? selectedJob.receipts : []),
        receipt,
      ];

      const receiptsTotal = receipts.reduce(
        (sum, item) => sum + Number(item.amount || 0),
        0
      );

      await updateDoc(doc(db, colName, selectedJob.id), {
        receipts,
        receiptsTotal,
      });

      setJobs((prev) =>
        prev.map((job) =>
          job.id === selectedJob.id && (job.__col || "jobs") === colName
            ? { ...job, receipts, receiptsTotal }
            : job
        )
      );

      setSelectedJob((prev) => ({
        ...prev,
        receipts,
        receiptsTotal,
      }));

      setShowReceiptPopup(false);
    } finally {
      setSavingReceipt(false);
    }
  };

  const getMaterialEstimatedCost = (material = {}) => {
    const qty = Number(material.qty || 0);
    const costPer = Number(material.costPer || 0);
    return qty * costPer;
  };

  const normalizeMaterial = (material = {}) => ({
    ...material,
    name: material.name || material.product || "",
    qty: material.qty ?? 1,
    unit: material.unit || "pcs",
    costPer: material.costPer ?? "",
    purchaseLocation: material.purchaseLocation || "",
    notes: material.notes || material.materialNotes || "",
    purchased: Boolean(material.purchased),
    files: Array.isArray(material.files) ? material.files : [],
  });

  const openMaterialsPopup = async () => {
    if (!selectedJob) return;
    const groupId = getMaterialsGroupId(selectedJob);
    const currentMaterials =
      jobMaterials[groupId] || (await loadMaterialsForJob(selectedJob));

    setJobMaterials((previous) => ({ ...previous, [groupId]: currentMaterials }));
    setMaterialsDraft(currentMaterials.map(normalizeMaterial));
    setShowMaterialsPopup(true);
  };

  const addMaterial = () => {
    setMaterialsDraft((prev) => [
      ...prev,
      normalizeMaterial({}),
    ]);
  };

  const uploadMaterialFiles = async (materialIndex, fileList) => {
    if (!selectedJob) return;
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const groupId = getMaterialsGroupId(selectedJob);
    const uploaded = [];

    for (const file of files) {
      const fileRef = ref(
        getStorage(),
        `materialFiles/${groupId}/${Date.now()}_${file.name}`
      );
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      uploaded.push({ url, name: file.name });
    }

    setMaterialsDraft((previous) =>
      previous.map((material, index) =>
        index === materialIndex
          ? { ...material, files: [...(material.files || []), ...uploaded] }
          : material
      )
    );
  };

  const removeMaterialFile = (materialIndex, fileIndex) => {
    setMaterialsDraft((previous) =>
      previous.map((material, index) =>
        index === materialIndex
          ? {
              ...material,
              files: (material.files || []).filter((_, fi) => fi !== fileIndex),
            }
          : material
      )
    );
  };

  const updateMaterialStatus = async (materialIndex, purchased) => {
    if (!selectedJob?.id) return;

    try {
      const groupId = getMaterialsGroupId(selectedJob);
      const currentMaterials = getMaterialsForJob(selectedJob);

      const nextMaterials = currentMaterials.map((material, index) =>
        index === materialIndex
          ? { ...material, purchased: Boolean(purchased) }
          : material
      );

      await saveMaterialsForJob(selectedJob, nextMaterials);

      setJobMaterials((previous) => ({
        ...previous,
        [groupId]: nextMaterials,
      }));

      setMaterialsDraft((prev) =>
        prev.map((material, index) =>
          index === materialIndex
            ? { ...material, purchased: Boolean(purchased) }
            : material
        )
      );
    } catch (error) {
      console.error("Error updating material purchase status:", error);
      alert("Could not update the material purchase status.");
    }
  };

  const saveMaterials = async () => {
    if (!selectedJob?.id) return;

    const groupId = getMaterialsGroupId(selectedJob);

    await saveMaterialsForJob(selectedJob, materialsDraft);

    setJobMaterials((previous) => ({
      ...previous,
      [groupId]: materialsDraft,
    }));

    setShowMaterialsPopup(false);
  };

  const saveQuickJobFields = async (changes) => {
    if (!selectedJob?.id) return;
    const colName = selectedJob.__col || "jobs";
    await updateDoc(doc(db, colName, selectedJob.id), { ...changes, updatedAt: serverTimestamp() });
    const updatedJob = { ...selectedJob, ...changes };
    setJobs((prev) => prev.map((job) =>
      job.id === selectedJob.id && (job.__col || "jobs") === colName ? { ...job, ...changes } : job
    ));
    setSelectedJob(updatedJob);
    setEditedJob(normalizeEditableJob(updatedJob));
    return true;
  };

  const saveJobEdits = async () => {
    if (!editedJob?.id) return;
  
    const colName = editedJob.__col || selectedJob?.__col || "jobs";
  
    setIsSavingJob(true);
    try {
      const {
        id,
        __col,
        createdAt,
        updatedAt,
        // add any other UI-only fields to exclude here
        ...rest
      } = editedJob;
  
      // ✅ normalize techAssign (string -> array)
      let techAssignNormalized = rest.techAssign;
      if (typeof techAssignNormalized === "string") {
        techAssignNormalized = techAssignNormalized
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
  
      const payloadToSave = {
        ...rest,
        techAssign: cleanTechAssign(rest.techAssign),
        updatedAt: serverTimestamp(),
      };
  
      const refDoc = doc(db, colName, editedJob.id);
      await updateDoc(refDoc, payloadToSave);
  
      // ✅ update UI immediately
      const updated = { ...editedJob, ...payloadToSave };
  
      setJobs((prev) => prev.map((j) => (j.id === editedJob.id ? updated : j)));
      setSelectedJob(updated);
      setEditedJob(updated);
  
      setIsEditingJob(false);
    } catch (err) {
      console.error("Error saving job edits:", err);
      alert("Failed to save changes. Check console.");
    } finally {
      setIsSavingJob(false);
    }
  };
  const dateValue = (v) => {
    if (!v) return "";
  
    // already a "YYYY-MM-DD..." string
    if (typeof v === "string") return v.slice(0, 10);
  
    // Firestore Timestamp
    if (v?.toDate) return moment(v.toDate()).format("YYYY-MM-DD");
  
    // Date object or number
    return moment(v).format("YYYY-MM-DD");
  };

  const formatNoteTimestamp = (note) => {
    if (note?.createdAt?.toDate) {
      return moment(note.createdAt.toDate()).format("MM-DD-YYYY hh:mm A");
    }
  
    if (note?.createdAt) {
      const m = moment(note.createdAt);
      if (m.isValid()) return m.format("MM-DD-YYYY hh:mm A");
    }
  
    if (note?.timestamp) return note.timestamp;
  
    return "N/A";
  };
  
  // ===================== HOME-COMPATIBLE JOB HISTORY DISPLAY =====================
  // Home saves normal notes to job.notes and recurring notes to
  // job.occurrenceNotes[YYYY-MM-DD]. Each note attachment is:
  // { fileURL, fileName }. Job-level uploads use parallel fileURLs/fileNames arrays.
  const isRecurringHistoryJob = (job) =>
    Boolean(job?.recurrence?.type && job.recurrence.type !== "none");

  const getHistoryNotesForDisplay = (job) => {
    if (!job) return [];

    let rows = [];

    // Standard / one-time notes — exact Home storage structure.
    if (Array.isArray(job.notes)) {
      job.notes.forEach((note, index) => {
        rows.push({
          ...note,
          __noteKey: `main_${index}`,
          __occurrenceDate: note?.occurrenceDate || "",
        });
      });
    }

    // Recurring notes — exact Home storage structure:
    // occurrenceNotes[date] = [{ text, timestamp, author, files: [...] }]
    if (job.occurrenceNotes && typeof job.occurrenceNotes === "object") {
      Object.entries(job.occurrenceNotes).forEach(([occurrenceDate, notes]) => {
        if (!Array.isArray(notes)) return;

        notes.forEach((note, index) => {
          rows.push({
            ...note,
            __noteKey: `occ_${occurrenceDate}_${index}`,
            __occurrenceDate: occurrenceDate,
          });
        });
      });
    }

    // WHICH notes belong on THIS row:
    //   • top row of a series (newest occurrence) → the WHOLE series' notes,
    //     so there is always one place that holds every note.
    //   • any older occurrence row → only the notes recorded on its own date.
    //   • a non-expanded / one-time job → everything, as before.
    if (job.__occurrenceRow && !job.__isSeriesTopRow) {
      const rowDate = toOccurrenceKey(job.occurrenceDate || job.startDate);

      if (rowDate) {
        rows = rows.filter(
          (note) => toOccurrenceKey(note.__occurrenceDate) === rowDate
        );
      }
    }

    // Remove exact duplicates only if the same note was copied into both structures.
    const seen = new Set();

    return rows
      .filter((note) => {
        const fileKey = Array.isArray(note.files)
          ? note.files.map((file) => `${file?.fileURL || ""}|${file?.fileName || ""}`).join("||")
          : "";

        const key = [
          note.__occurrenceDate || "",
          note.author || "",
          note.timestamp || "",
          note.text || "",
          fileKey,
        ].join("::");

        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => {
        const toMs = (note) => {
          if (Number.isFinite(Number(note?.createdAtMs))) return Number(note.createdAtMs);
          if (note?.createdAt?.toDate) return note.createdAt.toDate().getTime();

          const candidates = [
            note?.createdAtISO,
            note?.createdAt,
            note?.timestamp,
          ];

          for (const value of candidates) {
            if (!value) continue;
            const parsed = moment(value);
            if (parsed.isValid()) return parsed.valueOf();
          }

          return 0;
        };

        return toMs(a) - toMs(b);
      });
  };

  const getHomeCompatibleJobUploads = (job) => {
    const urls = Array.isArray(job?.fileURLs) ? job.fileURLs : [];
    const names = Array.isArray(job?.fileNames) ? job.fileNames : [];

    return urls
      .map((fileURL, index) => ({
        fileURL,
        fileName: names[index] || `View File ${index + 1}`,
      }))
      .filter((file) => Boolean(file.fileURL));
  };

  const isImageAttachment = (file = {}) => {
    const name = String(file.fileName || "");
    const url = String(file.fileURL || "");

    return (
      String(file.contentType || "").startsWith("image/") ||
      /\.(jpe?g|png|gif|webp|bmp|svg)(?:$|\?)/i.test(name) ||
      /\.(jpe?g|png|gif|webp|bmp|svg)(?:$|\?)/i.test(url)
    );
  };


  const handleNoteFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setNewNoteFiles(files);
  };
  
  const saveJobNote = async () => {
    if (!selectedJob?.id) return;
  
    const trimmedText = (newNoteText || "").trim();
  
    if (!trimmedText && newNoteFiles.length === 0) {
      alert("Please enter a note or upload at least one file.");
      return;
    }
  
    setIsSavingNote(true);
  
    try {
      const storage = getStorage();
      const colName = selectedJob.__col || "jobs";
  
      let uploadedFiles = [];
  
      if (newNoteFiles.length > 0) {
        uploadedFiles = await Promise.all(
          newNoteFiles.map(async (file) => {
            const safeName = `${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
            const storageRef = ref(storage, `jobNotes/${selectedJob.id}/${safeName}`);
            await uploadBytes(storageRef, file);
            const fileURL = await getDownloadURL(storageRef);
  
            return {
              fileName: file.name,
              fileURL,
              contentType: file.type || "",
              uploadedAt: moment().format("MM-DD-YYYY hh:mm A"),
            };
          })
        );
      }
  
      const now = moment();

      const newNote = {
        author: userFullName || userRole || "Unknown User",
        text: trimmedText,
        timestamp: now.format("MM-DD-YYYY hh:mm A"),
        createdAtISO: now.toISOString(),
        createdAtMs: now.valueOf(),
        files: uploadedFiles,
      };
  
      const refDoc = doc(db, colName, selectedJob.id);
  
      await updateDoc(refDoc, {
        notes: arrayUnion(newNote),
        updatedAt: serverTimestamp(),
      });
  
      const updatedJob = {
        ...selectedJob,
        notes: [...(selectedJob.notes || []), newNote],
      };
  
      setSelectedJob(updatedJob);
      setJobs((prev) =>
        prev.map((j) =>
          j.id === selectedJob.id && (j.__col || "jobs") === colName ? updatedJob : j
        )
      );
  
      setShowAddNote(false);
      setNewNoteText("");
      setNewNoteFiles([]);
    } catch (error) {
      console.error("Error saving note:", error);
      alert("Failed to save note.");
    } finally {
      setIsSavingNote(false);
    }
  };

  // ===================== RENDER HUB =====================
  return (
    
    <div style={{ padding: 14 }}>
            {/* ✅ Language toggle (like Home.js) */}
            <div style={{ textAlign: "right", marginBottom: 10 }}>
        <button
          onClick={() => setLanguage((prev) => (prev === "en" ? "es" : "en"))}
          style={{
            padding: "6px 12px",
            borderRadius: 8,
            border: "1px solid #1d4ed8",
            background: "#e0e7ff",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          {language === "en" ? "Español" : "English"}
        </button>
      </div>

      

      {/* TOP NAV like Home */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 10,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
       <button onClick={() => navigate("/")} style={topNavBtnStyle("/")}>
  Home
</button>

<button onClick={() => navigate("/JobHistory")} style={topNavBtnStyle("/JobHistory")}>
  Team Dashboard
</button>

<button onClick={handleAdminPortalClick} style={topNavBtnStyle("/SupervisorDashboard")}>
  Admin Portal
</button>

      </div>

      {/* ✅ Welcome + Role (like Home.js) */}
      <h1 className="center-text">{t("welcome")}</h1>
      <p className="center-text">
        {t("role")}: {userRole || "—"}
      </p>

      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12 }}>
        <h2 style={{ margin: 0 }}>Team Dashboard</h2>

        
      </div>

      {/* ✅ HOME-STYLE TAB HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 12,
          marginTop: 12,
          marginBottom: 0,
        }}
      >
        {/* LEFT: Tabs */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={() => setActiveTab("job-history")}
            style={navTabStyle(activeTab === "job-history")}
          >
            JobHistory
          </button>
          <button
  onClick={() => setActiveTab("scheduling")}
  style={navTabStyle(activeTab === "scheduling")}
>
  Scheduling
</button>

          <button
  onClick={() => {
    setActiveTab("proposals");
    setActiveProposalTab("ppd");
    setActiveProposalStatus("approved");
    setProposalView("main");
  }}
  
  style={navTabStyle(activeTab === "proposals")}
>
  Proposals
</button>


          <button
            onClick={() => setActiveTab("notebook")}
            style={navTabStyle(activeTab === "notebook")}
          >
            Notebook
          </button>


<button
  onClick={() => setActiveTab("reports")}
  style={navTabStyle(activeTab === "reports")}
>
  Reports
</button>

        </div>

        {/* RIGHT: More menu button */}
        <button
          onClick={() => setShowMoreMenu(true)}
          style={{
            padding: "10px 12px",
            borderRadius: "10px 10px 0 0",
            border: "1px solid #1d4ed8",
            background: "#e0e7ff",
            fontWeight: 700,
            cursor: "pointer",
            color: "#000",
            whiteSpace: "nowrap",
          }}
        >
          ⋯ More
        </button>
      </div>

      {/* ✅ CONTENT WRAPPER (like Home.js) */}
<div
  style={{
    border: "1px solid #1d4ed8",
    borderRadius: "0 10px 10px 10px",
    padding: 16,
    background: "#fff",
    minHeight: 500,

    // ✅ ADD THESE
    maxHeight: "calc(100vh - 240px)",
    overflowY: "auto",
  }}
>

        {/* JOB HISTORY EMBED (your existing UI) */}
        {activeTab === "job-history" && (
          <div className="dashboard-section-container">
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <h2 style={{ marginBottom: 12 }}>Job History</h2>

              <button className="smallazul-button" onClick={exportSiteToExcel}>
                Export Excel
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "360px 1fr",
                gap: 12,
                alignItems: "start",
              }}
            >
              {/* LEFT: Sites */}
              <div className="dashboard-section2" style={{ padding: 12 }}>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: "block" }}>
                    Search Sites
                  </label>
                  <input
                    type="text"
                    placeholder="Search sites…"
                    value={siteSearch}
                    onChange={(e) => setSiteSearch(e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: "block" }}>
                    Sort Sites By
                  </label>
                  <select value={siteSort} onChange={(e) => setSiteSort(e.target.value)} style={{ width: "100%" }}>
                    <option value="siteNameAsc">Site (A–Z)</option>
                    <option value="siteNameDesc">Site (Z–A)</option>
                    <option value="countDesc">Count (High–Low)</option>
                    <option value="countAsc">Count (Low–High)</option>
                  </select>
                </div>

                <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 8 }}>Sites ({sites.length})</div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                  {!showAllJobs ? (
                    <button
                      type="button"
                      className="smallazul-button"
                      onClick={() => {
                        setShowAllJobs(true);
                        setSelectedSite(null);
                      }}
                      style={{ width: "100%" }}
                    >
                      Show All Jobs
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="red-button"
                      onClick={() => {
                        setShowAllJobs(false);
                        setJobSearch("");
                      }}
                      style={{ width: "100%" }}
                    >
                      Clear All Jobs
                    </button>
                  )}
                </div>

                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {sites.map((s) => {
                    const isActive = selectedSite === s.siteName;
                    return (
                      <li
                        key={s.siteName}
                        onClick={() => {
                          setShowAllJobs(false);
                          setSelectedSite(s.siteName);
                          setJobSearch("");
                        }}
                        style={{
                          cursor: "pointer",
                          padding: "10px 10px",
                          borderRadius: 8,
                          marginBottom: 8,
                          border: isActive ? "2px solid #1e88e5" : "1px solid rgba(0,0,0,0.12)",
                          background: isActive ? "rgba(30,136,229,0.08)" : "white",
                        }}
                        title="Click to view work history"
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                          <strong style={{ fontSize: 14 }}>{s.siteName}</strong>
                          <span style={{ fontWeight: 700 }}>{s.count}</span>
                        </div>

                        <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                          Last activity: {s.lastActivityDate ? s.lastActivityDate.format("MM/DD/YYYY") : "N/A"}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* RIGHT: Work History for selected site */}
              <div className="dashboard-section2" style={{ padding: 12 }}>
                {!selectedSite && !showAllJobs ? (
                  <p style={{ margin: 0, opacity: 0.8 }}>Select a site on the left or click Show All Jobs to view job history.</p>
                ) : (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                      <div>
                        <h2 style={{ margin: 0 }}>{showAllJobs ? "All Jobs" : selectedSite}</h2>

                        <div style={{ fontSize: 13, opacity: 0.8 }}>
                          Jobs: {selectedSiteJobs.length}
                        </div>

                        {isAdmin && (
                          <>
                            <div style={{ fontSize: 13, opacity: 0.8 }}>
                              View Total: {money(siteViewTotal)} | View Net: {money(siteViewNetTotal)}
                            </div>

                            <div style={{ fontSize: 12, opacity: 0.75 }}>
                              This Month: {money(siteMonthlyTotal)} | YTD: {money(siteYTDTotal)}
                            </div>
                          </>
                        )}

                      </div>

                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <input
                          type="text"
                          placeholder="Search this site’s jobs…"
                          value={jobSearch}
                          onChange={(e) => setJobSearch(e.target.value)}
                          style={{ minWidth: 240 }}
                        />
                        <select value={jobSort} onChange={(e) => setJobSort(e.target.value)}>
                          <option value="startDateDesc">Start Date (New–Old)</option>
                          <option value="startDateAsc">Start Date (Old–New)</option>
                          <option value="woAsc">WO (A–Z)</option>
                          <option value="woDesc">WO (Z–A)</option>
                          <option value="statusAsc">Status (A–Z)</option>
                        </select>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                      <div>
                        <label style={{ fontSize: 12 }}>From</label>
                        <input type="date" value={jobStartDate} onChange={(e) => setJobStartDate(e.target.value)} />
                      </div>

                      <div>
                        <label style={{ fontSize: 12 }}>To</label>
                        <input type="date" value={jobEndDate} onChange={(e) => setJobEndDate(e.target.value)} />
                      </div>

                      {(jobStartDate || jobEndDate) && (
                        <button
                          onClick={() => {
                            setJobStartDate("");
                            setJobEndDate("");
                          }}
                        >
                          Clear Dates
                        </button>
                      )}
                    </div>

                    <div style={{ marginTop: 12 }}>
                      {selectedSiteJobs.length === 0 ? (
                        <p>No jobs found for this site (with current search).</p>
                      ) : (
                        <ul>
                          {selectedSiteJobs.map((job) => (
                            <li key={job.__rowKey || job.id} style={{ backgroundColor: getBackgroundColor(job) }}>
                              <strong>WO#:</strong> {job.wo || "N/A"}
                              {job.__occurrenceRow && (
                                <>
                                  {" "}
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      padding: "2px 6px",
                                      borderRadius: 4,
                                      background: job.__isSeriesTopRow ? "#1d4ed8" : "#e2e8f0",
                                      color: job.__isSeriesTopRow ? "#fff" : "#0f172a",
                                    }}
                                    title={
                                      job.__isSeriesTopRow
                                        ? "Most recent occurrence — holds all notes for this recurring job"
                                        : "One occurrence of a recurring job"
                                    }
                                  >
                                    {job.__isSeriesTopRow
                                      ? "Recurring · all notes"
                                      : `Recurring · ${moment(job.occurrenceDate).format("MM-DD-YYYY")}`}
                                  </span>
                                </>
                              )}
                              <br />
                              <strong>Status:</strong> {job.status || "N/A"}
                              <br />
                              <strong>Service Type:</strong> {job.serviceType || "N/A"}
                              <br />
                              <strong>Start Date:</strong> {job.startDate || "N/A"}
                              <br />
                              <strong>End Date:</strong> {job.endDate || "N/A"}
                              <br />
                              <strong>Scope:</strong> {job.scope || "N/A"}
                              <br />
                              <strong>Tech:</strong> {job.techAssign || "N/A"}
                              <br />
                              <strong>Total:</strong> ${getJobTotal(job).toLocaleString()}
                              <br />
                              <button
  className="smallazul-button"
  onClick={() => {
    setSelectedJob(job);
    setEditedJob(normalizeEditableJob(job));
    setIsEditingJob(false);
  }}
>
  Details
</button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* DETAILS MODAL */}
{selectedJob && (
  <>
    {createPortal(
      <>
        <div
          onClick={closeModal}
          style={{
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.55)",
            zIndex: 999998,
          }}
        ></div>

        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            margin: 0,
            width: "min(1000px, calc(100vw - 40px))",
            maxHeight: "85vh",
            overflowY: "auto",
            background: "#fff",
            padding: 20,
            borderRadius: 8,
            boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
            textAlign: "left",
            boxSizing: "border-box",
            zIndex: 999999,
          }}
        >
    <h2>Job Details {isEditingJob ? "(Editing)" : ""}</h2>

    {!isEditingJob && ["Admin", "Manager", "Assistant"].includes(userRole) && (
      <QuickJobFieldEditor job={selectedJob} usersList={usersList} onSave={saveQuickJobFields} />
    )}

     {/* ✅ BASIC FIELDS */}
<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
  <div>
    <label style={{ fontWeight: 800 }}>Site Name</label>
    {isEditingJob ? (
      <select
        value={editedJob?.siteName || ""}
        onChange={(e) => handleEditSiteChange(e.target.value)}
        style={{ width: "100%" }}
      >
        <option value="">Select Site</option>
        {[...propertiesList]
          .sort((a, b) =>
            String(a.propertyName || a.siteName || "").localeCompare(
              String(b.propertyName || b.siteName || "")
            )
          )
          .map((property) => {
            const label = property.propertyName || property.siteName || "";
            return (
              <option key={property.id} value={label}>
                {label}
              </option>
            );
          })}
      </select>
    ) : (
      <p>{selectedJob.siteName || "N/A"}</p>
    )}
  </div>

  <div>
    <label style={{ fontWeight: 800 }}>Work Order #</label>
    {isEditingJob ? (
      <input
        value={editedJob?.wo || ""}
        onChange={(e) => onEditJobChange("wo", e.target.value)}
        style={{ width: "100%" }}
      />
    ) : (
      <p>{selectedJob.wo || "N/A"}</p>
    )}
  </div>

  <div>
    <label style={{ fontWeight: 800 }}>Client Work Order #</label>
    {isEditingJob ? (
      <input
        value={editedJob?.clientWorkOrder || ""}
        onChange={(e) => onEditJobChange("clientWorkOrder", e.target.value)}
        style={{ width: "100%" }}
      />
    ) : (
      <p>{selectedJob.clientWorkOrder || "N/A"}</p>
    )}
  </div>

  <div>
    <label style={{ fontWeight: 800 }}>Status</label>
    {isEditingJob ? (
      <select
        value={editedJob?.status || ""}
        onChange={(e) => onEditJobChange("status", e.target.value)}
        style={{ width: "100%" }}
      >
        <option value="">Select Status</option>
        {statusOptions.map((status) => (
          <option key={status} value={status}>
            {status}
          </option>
        ))}
      </select>
    ) : (
      <p>{selectedJob.status || "N/A"}</p>
    )}
  </div>

  <div>
    <label style={{ fontWeight: 800 }}>Service Type</label>
    {isEditingJob ? (
      <select
        value={editedJob?.serviceType || ""}
        onChange={(e) => onEditJobChange("serviceType", e.target.value)}
        style={{ width: "100%" }}
      >
        <option value="">Select Service Type</option>
        {serviceTypes.map((type) => (
          <option key={type} value={type}>
            {type}
          </option>
        ))}
      </select>
    ) : (
      <p>{selectedJob.serviceType || "N/A"}</p>
    )}
  </div>

  <div>
    <label style={{ fontWeight: 800 }}>Start Date</label>
    {isEditingJob ? (
      <input
        type="date"
        value={dateValue(editedJob?.startDate)}
        onChange={(e) => onEditJobChange("startDate", e.target.value)}
        style={{ width: "100%" }}
      />
    ) : (
      <p>{fmtDate(selectedJob.startDate)}</p>
    )}
  </div>

  <div>
    <label style={{ fontWeight: 800 }}>End Date</label>
    {isEditingJob ? (
      <input
        type="date"
        value={dateValue(editedJob?.endDate)}
        onChange={(e) => onEditJobChange("endDate", e.target.value)}
        style={{ width: "100%" }}
      />
    ) : (
      <p>{fmtDate(selectedJob.endDate)}</p>
    )}
  </div>

  <div>
    <label style={{ fontWeight: 800 }}>Start Time</label>
    {isEditingJob ? (
      <>
        <select
          value={
            editedJob?.startTimeMode === "custom"
              ? "Custom Time"
              : editedJob?.startTime || ""
          }
          onChange={(e) => {
            const value = e.target.value;

            if (value === "Custom Time") {
              setEditedJob((prev) => ({
                ...(prev || {}),
                startTimeMode: "custom",
                startTime: "",
              }));
            } else {
              setEditedJob((prev) => ({
                ...(prev || {}),
                startTimeMode: "preset",
                startTime: value,
              }));
            }
          }}
          style={{ width: "100%" }}
        >
          <option value="">Select Start Time</option>
          {timeWindowOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        {editedJob?.startTimeMode === "custom" && (
          <input
            type="time"
            value={editedJob?.startTime || ""}
            onChange={(e) => onEditJobChange("startTime", e.target.value)}
            style={{ width: "100%", marginTop: 8 }}
          />
        )}
      </>
    ) : (
      <p>{fmtTime(selectedJob.startTime)}</p>
    )}
  </div>

  <div>
    <label style={{ fontWeight: 800 }}>End Time</label>
    {isEditingJob ? (
      <input
        type="time"
        value={editedJob?.endTime || ""}
        onChange={(e) => onEditJobChange("endTime", e.target.value)}
        style={{ width: "100%" }}
      />
    ) : (
      <p>{fmtTime(selectedJob.endTime)}</p>
    )}
  </div>

  <div style={{ gridColumn: "1 / -1" }}>
    <label style={{ fontWeight: 800, display: "block", marginBottom: 6 }}>
      Tech Assign
    </label>
    {isEditingJob ? (
      <>
        {normalizeTechAssign(editedJob?.techAssign).map((tech, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <select
              value={tech}
              onChange={(e) =>
                updateEditedTechnicianField(index, e.target.value)
              }
              style={{ flex: 1 }}
            >
              <option value="">Select Technician</option>
              {usersList.map((user) => {
                const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();

                return (
                  <option key={user.id} value={name}>
                    {name}
                  </option>
                );
              })}
            </select>

            <button
              type="button"
              className="red-button"
              onClick={() => removeEditedTechnicianField(index)}
              style={{
                width: 44,
                minWidth: 44,
                height: 44,
                padding: 0,
                fontSize: 18,
                fontWeight: "bold",
              }}
              title="Remove technician"
            >
              X
            </button>
          </div>
        ))}

        <button
          type="button"
          className="smallazul-button"
          onClick={addEditedTechnicianField}
        >
          Add Technician
        </button>
      </>
    ) : (
      <p>
        {Array.isArray(selectedJob.techAssign)
          ? selectedJob.techAssign.join(", ")
          : selectedJob.techAssign || "N/A"}
      </p>
    )}
  </div>
</div>

<hr />

<div>
  <label style={{ fontWeight: 800 }}>Scope</label>
  {isEditingJob ? (
    <input
      value={editedJob?.scope || ""}
      onChange={(e) => onEditJobChange("scope", e.target.value)}
      style={{ width: "100%" }}
    />
  ) : (
    <p>{selectedJob.scope || "N/A"}</p>
  )}
</div>

<div style={{ marginTop: 10 }}>
  <label style={{ fontWeight: 800 }}>Job Description</label>
  {isEditingJob ? (
    <textarea
      value={editedJob?.jobDescription || ""}
      onChange={(e) => onEditJobChange("jobDescription", e.target.value)}
      style={{ width: "100%", minHeight: 120 }}
    />
  ) : (
    <div
      style={{
        marginTop: 8,
        padding: "12px 14px",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        background: "#f9fafb",
        textAlign: "left",
      }}
    >
      {renderProposalJobDescription(selectedJob.jobDescription)}
    </div>
  )}
</div>

      <p>
        <strong>Created By:</strong>{" "}
        {selectedJob.createdBy?.fullName ||
          [selectedJob.createdBy?.firstName, selectedJob.createdBy?.lastName]
            .filter(Boolean)
            .join(" ") ||
          selectedJob.createdByName ||
          selectedJob.creatorName ||
          selectedJob.createdByEmail ||
          "Unknown"}
      </p>

      <p>
        <strong>Created On:</strong>{" "}
        {selectedJob.creationDate?.seconds
          ? moment(selectedJob.creationDate.seconds * 1000).format("MM-DD-YYYY hh:mm A")
          : "N/A"}
      </p>

      <hr />

      <h3>Notes</h3>
<div style={{ marginBottom: 14 }}>
  {!showAddNote ? (
    <button
      className="smallazul-button"
      onClick={() => setShowAddNote(true)}
    >
      Add Note
    </button>
  ) : (
    <div
      style={{
        border: "1px solid #cbd5e1",
        borderRadius: 10,
        padding: 12,
        background: "#f8fafc",
      }}
    >
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontWeight: 700, display: "block", marginBottom: 6 }}>
          Note
        </label>
        <textarea
          value={newNoteText}
          onChange={(e) => setNewNoteText(e.target.value)}
          placeholder="Type your note here..."
          style={{ width: "100%", minHeight: 100 }}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={{ fontWeight: 700, display: "block", marginBottom: 6 }}>
          Upload File(s)
        </label>
        <input
          type="file"
          multiple
          onChange={handleNoteFileChange}
        />

        {newNoteFiles.length > 0 && (
          <ul style={{ marginTop: 8, paddingLeft: 18 }}>
            {newNoteFiles.map((file, i) => (
              <li key={`${file.name}-${i}`}>{file.name}</li>
            ))}
          </ul>
        )}
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button
          className="smallazul-button"
          onClick={saveJobNote}
          disabled={isSavingNote}
        >
          {isSavingNote ? <LoadingSpinner label="Saving note..." /> : "Save Note"}
        </button>

        <button
          className="red-button"
          onClick={() => {
            setShowAddNote(false);
            setNewNoteText("");
            setNewNoteFiles([]);
          }}
          disabled={isSavingNote}
        >
          Cancel
        </button>
      </div>
    </div>
  )}
</div>

{getHistoryNotesForDisplay(selectedJob).length > 0 ? (
  <ul style={{ paddingLeft: 16 }}>
    {getHistoryNotesForDisplay(selectedJob).map((note, i) => (
      <li key={note.__noteKey || i} style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
            marginBottom: 4,
          }}
        >
          <div style={{ fontWeight: 700 }}>
            Added by: {note.author || "Unknown User"}
          </div>

          <div style={{ fontSize: 13, opacity: 0.75 }}>
            {formatNoteTimestamp(note)}
          </div>
        </div>

        {note.__occurrenceDate && (
          <div
            style={{
              display: "inline-block",
              marginBottom: 6,
              padding: "3px 8px",
              borderRadius: 999,
              background: "#dbeafe",
              color: "#1e3a8a",
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            Recurring occurrence: {moment(note.__occurrenceDate).format("MM-DD-YYYY")}
          </div>
        )}

        {note.text && (
          <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>
            {note.text}
          </div>
        )}

        {/* Exact Home note attachment structure: note.files[].fileURL / fileName */}
        {Array.isArray(note.files) && note.files.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {note.files.map((file, idx) => {
              if (!file?.fileURL) return null;
              const image = isImageAttachment(file);
              const displayName =
                file.fileName || `View Attachment ${idx + 1}`;

              return (
                <div key={`${file.fileURL}_${idx}`} style={{ marginTop: 8 }}>
                  {image && (
                    <img
                      src={file.fileURL}
                      alt={displayName}
                      onClick={() =>
                        setPreviewImage({
                          url: file.fileURL,
                          name: displayName,
                        })
                      }
                      style={{
                        maxWidth: 180,
                        maxHeight: 180,
                        borderRadius: 6,
                        border: "1px solid #ccc",
                        display: "block",
                        marginBottom: 6,
                        cursor: "pointer",
                        objectFit: "cover",
                      }}
                      title="Click to preview image"
                    />
                  )}

                  <a
                    href={file.fileURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontWeight: 700, color: "#1d4ed8" }}
                  >
                    {displayName}
                  </a>

                  {image && (
                    <button
                      type="button"
                      className="smallazul-button"
                      onClick={() =>
                        setPreviewImage({
                          url: file.fileURL,
                          name: displayName,
                        })
                      }
                      style={{ marginLeft: 8 }}
                    >
                      Preview
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </li>
    ))}
  </ul>
) : (
  <p>No notes available.</p>
)}

{/* Exact Home job-level upload structure: fileURLs[] paired with fileNames[] */}
{getHomeCompatibleJobUploads(selectedJob).length > 0 && (
  <div style={{ marginTop: 18 }}>
    <h3>Uploaded Files</h3>

    <div className="uploaded-files">
      {getHomeCompatibleJobUploads(selectedJob).map((file, index) => {
        const image = isImageAttachment(file);

        return (
          <div
            key={`${file.fileURL}_${index}`}
            style={{
              marginBottom: 12,
              padding: 8,
              border: "1px solid #e2e8f0",
              borderRadius: 8,
            }}
          >
            {image && (
              <img
                src={file.fileURL}
                alt={file.fileName}
                onClick={() =>
                  setPreviewImage({
                    url: file.fileURL,
                    name: file.fileName,
                  })
                }
                style={{
                  maxWidth: 180,
                  maxHeight: 180,
                  borderRadius: 6,
                  border: "1px solid #ccc",
                  display: "block",
                  marginBottom: 6,
                  cursor: "pointer",
                  objectFit: "cover",
                }}
                title="Click to preview image"
              />
            )}

            <a
              href={file.fileURL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontWeight: 700, color: "#1d4ed8" }}
            >
              {file.fileName}
            </a>

            {image && (
              <button
                type="button"
                className="smallazul-button"
                onClick={() =>
                  setPreviewImage({
                    url: file.fileURL,
                    name: file.fileName,
                  })
                }
                style={{ marginLeft: 8 }}
              >
                Preview
              </button>
            )}
          </div>
        );
      })}
    </div>
  </div>
)}

{canSeeSupervisorNotes && (
  <div
    style={{
      marginTop: 16,
      padding: 12,
      border: "2px solid #7c3aed",
      borderRadius: 10,
      background: "#f5f3ff",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <h3 style={{ marginTop: 0 }}>Supervisor Notes</h3>
    </div>

    {Array.isArray(selectedJob.supervisorNotes) &&
    selectedJob.supervisorNotes.length > 0 ? (
      <ul style={{ paddingLeft: 18 }}>
        {selectedJob.supervisorNotes.map((note) => (
          <li
            key={note.id}
            style={{
              marginBottom: 10,
              padding: 8,
              background: "white",
              border: "1px solid #c4b5fd",
              borderRadius: 8,
            }}
          >
            <div>
  <div style={{ whiteSpace: "pre-wrap" }}>{note.text}</div>

  <div style={{ fontSize: 12, color: "#4b5563", marginTop: 4 }}>
    Added by {note.author || "User"}
    {note.authorRole ? ` (${note.authorRole})` : ""}{" "}
    {note.createdAt
      ? `on ${new Date(note.createdAt).toLocaleString()}`
      : ""}
  </div>
</div>
          </li>
        ))}
      </ul>
    ) : (
      <p>No supervisor notes yet.</p>
    )}
  </div>
)}

<div>
{!isEditingJob ? (
  <>
    <CollapsibleActionSection title="Supervisor" tone="supervisor" defaultOpen={false}>
      <button
        className="smallazul-button"
        onClick={() => {
          setIsEditingJob(true);
          setEditedJob(normalizeEditableJob(selectedJob));
        }}
      >
        Edit
      </button>

      {canSeeSupervisorNotes && (
        <button className="grey-button" onClick={addSupervisorNote}>
          Add Supervisor Note
        </button>
      )}

      {!isRecurringJobRecord(selectedJob) && selectedJob.status !== "Completed" && (
        <button className="yellow-button" onClick={continueJob}>
          Continue
        </button>
      )}

      <button className="green-button" onClick={openTotalPopup}>
        {getJobTotal(selectedJob) ? "Change Total" : "Add Total"}
      </button>

      <button
        className="red-button"
        onClick={handleDeleteJob}
        style={{ backgroundColor: "#b91c1c" }}
      >
        Close Job
      </button>
    </CollapsibleActionSection>

    <div className="modal-buttons" style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
      <button
        className="green-button"
        onClick={() =>
          // Jobs open the Complete Job & Create Invoice form. Items from the
          // addItem list aren't jobs, so they keep the simple confirmation.
          (selectedJob.__col || "jobs") === "jobs"
            ? setShowInvoiceModal(true)
            : setShowVerificationPopup(true)
        }
      >
        Mark Job as Completed
      </button>
    </div>

    <div className="modal-buttons" style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
      <button className="blue-button" onClick={openReceiptPopup}>
        Add Receipt
      </button>
      <button className="blue-button" onClick={openMaterialsPopup}>
        Add / Edit Materials
      </button>
      <button className="grey-button" onClick={() => setShowHistoryPopup(true)}>
        History
      </button>
      <button
        className="yellow-button"
        onClick={() =>
          navigate(`/JobChecklist/${selectedJob.id}`, {
            state: {
              returnTo: `${location.pathname}${location.search || ""}`,
              reopenJobId: selectedJob.id,
              reopenOccurrenceDate: selectedJob.occurrenceDate || selectedJob.startDate || "",
            },
          })
        }
      >
        Checklist
      </button>
      <button className="red-button" onClick={closeModal}>
        Close
      </button>
    </div>
  </>
) : (
  <div className="modal-buttons" style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
    <button
      className="smallazul-button"
      onClick={saveJobEdits}
      disabled={isSavingJob}
    >
      {isSavingJob ? "Saving..." : "Save Changes"}
    </button>

    <button
        className="red-button"
        onClick={() => {
          setIsEditingJob(false);
          setEditedJob({ ...selectedJob });
        }}
        disabled={isSavingJob}
      >
        Cancel
      </button>
  </div>
  )}
</div>
</div>
      </>,
      document.body
    )}

{showHistoryPopup && (
  <JobHistoryPopup
    job={selectedJob}
    jobs={jobs}
    onClose={() => setShowHistoryPopup(false)}
  />
)}

{showInvoiceModal && selectedJob && (
  <JobCompletionInvoiceModal
    job={selectedJob}
    onClose={() => {
      setShowInvoiceModal(false);
      if (completionSubmitted) {
        setCompletionSubmitted(false);
        setSelectedJob(null);
        refreshKeepingView();
      }
    }}
    onCompleted={() => setCompletionSubmitted(true)}
  />
)}

{showVerificationPopup && createPortal(
  <>
    <div
      onClick={closeVerificationPopup}
      style={{
        position: "fixed", inset: 0, width: "100vw", height: "100vh",
        background: "rgba(0,0,0,0.55)", zIndex: 1999998,
      }}
    ></div>
    <div
      style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", margin: 0,
        width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
        background: "#fff", padding: 20, borderRadius: 8,
        boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
        boxSizing: "border-box", zIndex: 1999999,
      }}
    >
      <h2>Mark Job as Completed</h2>
      <p>Are you sure you want to mark this job as completed?</p>
      <div className="modal-buttons">
        <button className="green-button" onClick={markJobCompleted}>Yes</button>
        <button className="red-button" onClick={closeVerificationPopup}>No</button>
      </div>
    </div>
  </>,
  document.body
)}

{showTotalPopup && selectedJob && createPortal(
  <>
    <div
      onClick={() => setShowTotalPopup(false)}
      style={{
        position: "fixed", inset: 0, width: "100vw", height: "100vh",
        background: "rgba(0,0,0,0.55)", zIndex: 1999998,
      }}
    ></div>
    <div
      style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", margin: 0,
        width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
        background: "#fff", padding: 20, borderRadius: 8,
        boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
        boxSizing: "border-box", zIndex: 1999999,
      }}
    >
      <h2>{getJobTotal(selectedJob) ? "Change Total" : "Add Total"}</h2>
      <input
        type="number"
        value={totalInput}
        onChange={(e) => setTotalInput(e.target.value)}
      />
      <div className="modal-buttons">
        <button className="green-button" onClick={saveTotal}>Save</button>
        <button className="red-button" onClick={() => setShowTotalPopup(false)}>Cancel</button>
      </div>
    </div>
  </>,
  document.body
)}

{showReceiptPopup && createPortal(
  <>
    <div
      onClick={() => setShowReceiptPopup(false)}
      style={{
        position: "fixed", inset: 0, width: "100vw", height: "100vh",
        background: "rgba(0,0,0,0.55)", zIndex: 1999998,
      }}
    ></div>
    <div
      style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", margin: 0,
        width: "90%", maxWidth: 500, maxHeight: "85vh", overflowY: "auto",
        background: "#fff", padding: 20, borderRadius: 8,
        boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
        boxSizing: "border-box", zIndex: 1999999,
      }}
    >
      <h2>Add Receipt</h2>
      <label>Amount</label>
      <input
        type="number"
        value={receiptAmount}
        onChange={(e) => setReceiptAmount(e.target.value)}
      />
      <label>Note</label>
      <input
        type="text"
        value={receiptNote}
        onChange={(e) => setReceiptNote(e.target.value)}
      />
      <label>Receipt File</label>
      <input
        type="file"
        accept="image/*,.pdf"
        onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
      />
      <div className="modal-buttons">
        <button className="green-button" onClick={saveReceipt} disabled={savingReceipt}>
          {savingReceipt ? "Saving..." : "Save Receipt"}
        </button>
        <button className="red-button" onClick={() => setShowReceiptPopup(false)}>Cancel</button>
      </div>
    </div>
  </>,
  document.body
)}

{showMaterialsPopup && createPortal(
  <>
    <div
      onClick={() => setShowMaterialsPopup(false)}
      style={{
        position: "fixed", inset: 0, width: "100vw", height: "100vh",
        background: "rgba(0,0,0,0.55)", zIndex: 1999998,
      }}
    ></div>

    <div
      style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", margin: 0,
        width: "min(1050px, 96vw)",
        maxHeight: "calc(100vh - 40px)",
        overflowY: "auto",
        background: "#fff", padding: 20, borderRadius: 8,
        boxShadow: "0 5px 15px rgba(0,0,0,0.3)", textAlign: "left",
        boxSizing: "border-box", zIndex: 1999999,
      }}
    >
      <h2 style={{ marginTop: 0 }}>Add / Edit Materials</h2>

      {materialsDraft.length === 0 && (
        <p style={{ fontStyle: "italic" }}>No materials have been added.</p>
      )}

      <div style={{ display: "grid", gap: 14 }}>
        {materialsDraft.map((material, index) => {
          const estimatedCost = getMaterialEstimatedCost(material);

          const updateDraftMaterial = (patch) => {
            setMaterialsDraft((prev) =>
              prev.map((item, itemIndex) =>
                itemIndex === index ? { ...item, ...patch } : item
              )
            );
          };

          return (
            <div
              key={index}
              style={{
                padding: 14,
                border: material.purchased
                  ? "2px solid #16a34a"
                  : "1px solid #cbd5e1",
                borderRadius: 12,
                background: material.purchased ? "#f0fdf4" : "#f8fafc",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <strong>Material {index + 1}</strong>

                <button
                  type="button"
                  className="red-button"
                  onClick={() =>
                    setMaterialsDraft((prev) =>
                      prev.filter((_, itemIndex) => itemIndex !== index)
                    )
                  }
                  style={{ width: "auto" }}
                >
                  Remove Material
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                  gap: 12,
                }}
              >
                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Product
                  <input
                    type="text"
                    value={material.name || ""}
                    onChange={(event) =>
                      updateDraftMaterial({ name: event.target.value })
                    }
                    placeholder="Product or material name"
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Qty
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={material.qty ?? ""}
                    onChange={(event) =>
                      updateDraftMaterial({ qty: event.target.value })
                    }
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Unit
                  <input
                    type="text"
                    value={material.unit || ""}
                    onChange={(event) =>
                      updateDraftMaterial({ unit: event.target.value })
                    }
                    placeholder="pcs, ft, bags"
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Cost Per
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #cbd5e1",
                      borderRadius: 6,
                      background: "#fff",
                      overflow: "hidden",
                    }}
                  >
                    <span style={{ paddingLeft: 10 }}>$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={material.costPer ?? ""}
                      onChange={(event) =>
                        updateDraftMaterial({ costPer: event.target.value })
                      }
                      style={{ border: 0, boxShadow: "none" }}
                    />
                  </div>
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Estimated Material Cost
                  <input
                    type="text"
                    readOnly
                    value={estimatedCost.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                    style={{ background: "#e2e8f0", fontWeight: 900 }}
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  Purchase Location
                  <input
                    type="text"
                    value={material.purchaseLocation || ""}
                    onChange={(event) =>
                      updateDraftMaterial({
                        purchaseLocation: event.target.value,
                      })
                    }
                    placeholder="Home Depot, Lowe's, supplier, etc."
                  />
                </label>
              </div>

              <label
                style={{
                  display: "grid",
                  gap: 5,
                  marginTop: 12,
                  fontWeight: 800,
                }}
              >
                Material Notes
                <textarea
                  rows="3"
                  value={material.notes || ""}
                  onChange={(event) =>
                    updateDraftMaterial({ notes: event.target.value })
                  }
                  placeholder="Sizes, model numbers, colors, special instructions, etc."
                />
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginTop: 12,
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={Boolean(material.purchased)}
                  onChange={(event) =>
                    updateDraftMaterial({ purchased: event.target.checked })
                  }
                  style={{ width: 20, height: 20 }}
                />
                Purchased
              </label>

              <div style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>
                  Photos / Files
                </div>

                {Array.isArray(material.files) && material.files.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                    {material.files.map((file, fileIndex) => (
                      <div key={fileIndex} style={{ position: "relative" }}>
                        <a href={file.url} target="_blank" rel="noopener noreferrer" title={file.name}>
                          {/\.(jpe?g|png|gif|webp)$/i.test(file.name || "") ? (
                            <img
                              src={file.url}
                              alt={file.name}
                              style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6, border: "1px solid #cbd5e1" }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 64,
                                height: 64,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px solid #cbd5e1",
                                borderRadius: 6,
                                background: "#fff",
                                fontSize: 10,
                                textAlign: "center",
                                padding: 4,
                                overflow: "hidden",
                              }}
                            >
                              {file.name}
                            </div>
                          )}
                        </a>
                        <button
                          type="button"
                          onClick={() => removeMaterialFile(index, fileIndex)}
                          title="Remove file"
                          style={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            border: "1px solid #b91c1c",
                            background: "#fee2e2",
                            color: "#b91c1c",
                            fontSize: 13,
                            lineHeight: 1,
                            padding: 0,
                            cursor: "pointer",
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={(event) => uploadMaterialFiles(index, event.target.files)}
                />
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="blue-button"
        onClick={addMaterial}
        style={{ marginTop: 14, width: "auto" }}
      >
        + Add Material
      </button>

      <div
        style={{
          marginTop: 14,
          padding: 12,
          borderRadius: 10,
          border: "2px solid #2563eb",
          background: "#eff6ff",
          fontSize: 17,
          fontWeight: 900,
        }}
      >
        Total Estimated Material Cost:{" "}
        {materialsDraft
          .reduce(
            (sum, material) => sum + getMaterialEstimatedCost(material),
            0
          )
          .toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          })}
      </div>

      <div className="modal-buttons">
        <button className="green-button" onClick={saveMaterials}>
          Save Materials
        </button>
        <button
          className="red-button"
          onClick={() => setShowMaterialsPopup(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  </>,
  document.body
)}

{previewImage && (
  <>
    <div
      className="modal-overlay"
      onClick={() => setPreviewImage(null)}
      style={{ zIndex: 1002 }}
    ></div>

    <div
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        background: "#fff",
        borderRadius: 12,
        padding: 16,
        zIndex: 1003,
        maxWidth: "90vw",
        maxHeight: "90vh",
        boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 10,
        }}
      >
        <strong style={{ wordBreak: "break-word" }}>
          {previewImage.name}
        </strong>

        <button
          className="red-button"
          onClick={() => setPreviewImage(null)}
        >
          Close
        </button>
      </div>

      <div style={{ textAlign: "center" }}>
        <img
          src={previewImage.url}
          alt={previewImage.name}
          style={{
            maxWidth: "100%",
            maxHeight: "75vh",
            borderRadius: 8,
          }}
        />
      </div>
    </div>
  </>
)}
</>
)}

          </div>
        )}

       {/* PROPOSALS EMBED */}
       {activeTab === "proposals" && (
<div
  style={{
    display: "flex",
    flexDirection: "column",
    gap: 12,
    minHeight: 0,      // ✅ IMPORTANT
  }}
>

    {/* TOP ROW: JMC / DCS / InvoicePPD */}
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        marginBottom: 16,
        marginTop: 6,
      }}
    >

      <button
        style={proposalChipBtn("ppd", activeProposalTab === "ppd")}
        onClick={() => {
          setActiveProposalTab("ppd");
          setActiveProposalStatus("approved");
          setProposalView("main");
        }}
        
      >
        ProposalsPPD
      </button>

      <button
        style={proposalChipBtn("invoiceppd", activeProposalTab === "invoiceppd")}
        onClick={() => {
          setActiveProposalTab("invoiceppd");
          setProposalView("main");          // ✅ keep proposals view clean
          setInvoiceView("main");           // ✅ show InvoicePPD first
          setActiveInvoiceStatus("approved");
        }}
        
      >
        InvoicePPD
      </button>
    </div>

    {/* ✅ JMC */}
    {activeProposalTab === "jmc" && (
  <div style={{ display: "grid", gap: 12 }}>
    {/* ✅ Status tabs above the content */}
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      <button
        style={chipBtn(proposalView === "main")}
        onClick={() => setProposalView("main")}
      >
        JMC Proposals
      </button>

      <button
        style={chipBtn(proposalView === "approved")}
        onClick={() => {
          setActiveProposalStatus("approved");
          setProposalView("approved");
        }}
      >
        Approved
      </button>

      <button
        style={chipBtn(proposalView === "completed")}
        onClick={() => {
          setActiveProposalStatus("completed");
          setProposalView("completed");
        }}
      >
        Completed
      </button>

      <button
        style={chipBtn(proposalView === "sent")}
        onClick={() => {
          setActiveProposalStatus("sent");
          setProposalView("sent");
        }}
      >
        Sent
      </button>
    </div>

    {/* ✅ Single content area (either ProposalsJMC or a status embed) */}
    <div style={embedWrap}>
      {proposalView === "main" && <ProposalsJMC />}

      {proposalView !== "main" && (
        <>
          {activeProposalStatus === "approved" && <ApprovedProposals />}
          {activeProposalStatus === "completed" && <CompletedProposals />}
          {activeProposalStatus === "sent" && <SentProposals />}
        </>
      )}
    </div>
  </div>
)}


    {/* ✅ DCS */}
    {activeProposalTab === "ppd" && (
  <div style={{ display: "grid", gap: 12 }}>
    {/* ✅ Status tabs above the content */}
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      <button
        style={chipBtn(proposalView === "main")}
        onClick={() => setProposalView("main")}
      >
        PPD Proposals
      </button>

      <button
        style={chipBtn(proposalView === "approved")}
        onClick={() => {
          setActiveProposalStatus("approved");
          setProposalView("approved");
        }}
      >
        Approved
      </button>

      <button
        style={chipBtn(proposalView === "completed")}
        onClick={() => {
          setActiveProposalStatus("completed");
          setProposalView("completed");
        }}
      >
        Completed
      </button>

      <button
        style={chipBtn(proposalView === "sent")}
        onClick={() => {
          setActiveProposalStatus("sent");
          setProposalView("sent");
        }}
      >
        Sent
      </button>
    </div>

    {/* ✅ Single content area */}
    <div style={embedWrap}>
      {proposalView === "main" && <ProposalsPPD />}

      {proposalView !== "main" && (
        <>
          {activeProposalStatus === "approved" && <PPDApprovedProposals />}
          {activeProposalStatus === "completed" && <PPDCompletedProposals />}
          {activeProposalStatus === "sent" && <PPDSentProposals />}
        </>
      )}
    </div>
  </div>
)}


    {/* ✅ INVOICE PPD */}
    {activeProposalTab === "invoiceppd" && (
  <div style={{ display: "grid", gap: 12 }}>
    {/* ✅ Invoice tabs */}
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      <button
        style={chipBtn(invoiceView === "main")}
        onClick={() => setInvoiceView("main")}
      >
        Invoices
      </button>

      <button
        style={chipBtn(invoiceView === "approved")}
        onClick={() => {
          setActiveInvoiceStatus("approved");
          setInvoiceView("approved");
        }}
      >
        Approved
      </button>

      <button
        style={chipBtn(invoiceView === "completed")}
        onClick={() => {
          setActiveInvoiceStatus("completed");
          setInvoiceView("completed");
        }}
      >
        Completed
      </button>

      <button
        style={chipBtn(invoiceView === "sent")}
        onClick={() => {
          setActiveInvoiceStatus("sent");
          setInvoiceView("sent");
        }}
      >
        Sent
      </button>
    </div>

    {/* ✅ Single content area */}
    <div style={embedWrap}>
      {invoiceView === "main" && <InvoicePPD />}

      {invoiceView !== "main" && (
        <>
          {activeInvoiceStatus === "approved" && <PPDApprovedInvoices />}
          {activeInvoiceStatus === "completed" && <PPDCompletedInvoices />}
          {activeInvoiceStatus === "sent" && <PPDSentInvoices />}
        </>
      )}
    </div>
  </div>
)}

</div>
)}


{/* SCHEDULING EMBED */}
{activeTab === "scheduling" && (
  <div
    style={{
      maxHeight: "calc(100vh - 260px)", // adjust if needed
      overflowY: "auto",
      paddingRight: 6,
    }}
  >
    <Scheduling />
  </div>
)}



        {/* NOTEBOOK EMBED */}
        {activeTab === "notebook" && <Notebook />}


        {/* SPREADSHEET EMBED */}


{activeTab === "reports" && <Reports />}

        {/* MORE MENU EMBEDS */}
        {activeTab === "vendors" && <Vendors />}
        {activeTab === "contractors" && <Contractors />}
        {activeTab === "vehicles" && <Vehicles />}
        {activeTab === "supplies" && <Supplies />}
      </div>

      {/* MORE MENU POPUP */}
      {showMoreMenu && (
        <>
          <div
            className="modal-overlay"
            onClick={() => setShowMoreMenu(false)}
            style={{ position: "fixed", inset: 0, zIndex: 999 }}
          ></div>

          <div
            style={{
              position: "fixed",
              top: "20%",
              left: "50%",
              transform: "translateX(-50%)",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
              padding: 12,
              zIndex: 1001,
              minWidth: 260,
            }}
          >
            {[
              { label: "Vendors", tab: "vendors" },
              { label: "Contractors", tab: "contractors" },
              { label: "Vehicles", tab: "vehicles" },
              { label: "Supplies", tab: "supplies" },
            ].map((x) => (
              <button
                key={x.tab}
                onClick={() => {
                  setShowMoreMenu(false);
                  setActiveTab(x.tab);
                }}
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  marginBottom: 8,
                  borderRadius: 10,
                  border: "1px solid #1d4ed8",
                  background: "#e0e7ff",
                  color: "#0f172a",
                  fontWeight: 800,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                {x.label}
              </button>
            ))}

            <div style={{ textAlign: "right", marginTop: 8 }}>
              <button onClick={() => setShowMoreMenu(false)} className="red-button">
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default JobHistory;
